// App-shell navigation: the console's instance of the FormantKit unified
// shell (`@cortex/ui-kit/shell-components.css`). One persistent nav rail
// (`.fk-navrail`) carries the IA from the 2026-07-07 nav review, updated
// 2026-07-09 after user feedback:
//
//   Incidents   Board · Coordinator (Board columns is reached from
//               the board toolbar)
//   Dashboards  All dashboards · Recent (folder: 5 most recently
//               opened-or-created, minus favorites) · Favorites (folder: the
//               user's starred dashboards — starred on the All dashboards
//               page); both A→Z, shine mark = updated in the last day
//   Automation   Managed Agents (folder: Agents · Skills · Memories)
//            Workflows
//            Evals (folder: Suites · Feedback · Evals · Judges · Ground truths)
//            Knowledge (folder: Workspaces · Skills · Knowledge graph ·
//                       ▸ Legacy [collapsible]: Document sets · Blueprints ·
//                       Pipelines · Knowledge graph (legacy))
//   Admin    Models · MCPs · App Connections · API keys
//   Internal (superuser only)
//
// Incidents leads — it's the operator's day-to-day queue. Automation is ordered as
// a wizard — set up agents, build workflows, eval them; knowledge sits last
// as the supporting library. Agents/Evals/Knowledge are collapsible nav
// folders (`.fk-navrail__folder`). Persistent Agents, Platform Costs, Refinement,
// Orchestrator, and Remote Realtime Tools are hidden behind org-level
// feature flags (superuser-managed, not deleted); the Users page left the
// nav for good (shared org with robotics).
import type { ConsoleFeatureFlags, DashboardListItem } from '@cortex/types';
import { Link, useRouterState } from '@tanstack/react-router';
import { Fragment, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';

import { getAuthMode } from '../../auth/auth-mode';
import { useIsSuperuser } from '../../hooks/useIsSuperuser';
import { useOrgRole } from '../../hooks/useOrgRole';
import { routes } from '../../lib/constants';
import { BotsNavSection } from '../bots/BotsNavSection';
import {
  hasUnseenUpdate,
  selectFavoriteDashboards,
  selectRecentDashboards,
} from '../../lib/dashboard-nav';
import { readDashboardVisits, subscribeDashboardVisits } from '../../lib/dashboard-recency';
import { useFeatureFlags } from '../../lib/feature-flags';
import { useNavDashboards } from '../../lib/use-nav-dashboards';

interface INavLink {
  label: string;
  to: string;
  /** Active match: a path is on this page if it equals `to` or starts with `prefix/`. */
  prefix: string;
  icon: ReactNode;
  superuserOnly?: boolean;
  /** Hidden when signed in via Coherence — a Keycloak-only admin surface
   *  (user/org management lives in the Formant platform, not this console). */
  coherenceHidden?: boolean;
  /** Trailing count pill (e.g. Inbox's pending-items badge); omitted/0 hides it. */
  badge?: number;
  /**
   * When set, this item belongs to a named collapsible sub-group rendered at
   * the tail of its folder (used to tuck the demoted "Legacy" entries behind
   * one toggle). All items sharing a label collapse together; the group
   * auto-opens when it contains the active page.
   */
  subgroup?: string;
  /** Small adornment after the label (e.g. the "recently updated" shine on dashboards). */
  trailingIcon?: ReactNode;
}

interface INavFolder {
  label: string;
  icon: ReactNode;
  /** First visible child doubles as the folder's target in the collapsed rail. */
  children: INavLink[];
  /**
   * Pinned open: always expanded, chevron toggling disabled. Used by
   * Dashboards > Recent/Favorites — they're short, curated lists that act as
   * quick launchers, so hiding them behind a click defeats the point.
   */
  alwaysExpanded?: boolean;
}

type NavEntry = INavLink | INavFolder;

interface INavSection {
  label: string;
  entries: NavEntry[];
  superuserOnly?: boolean;
}

function isFolder(entry: NavEntry): entry is INavFolder {
  return 'children' in entry;
}

// ── Icon glyphs ────────────────────────────────────────────────────────────
// All inline (Lucide-style 24px viewBox), tinted via currentColor. The
// `icon` class lets rail/topbar contexts size them via the kit's CSS
// (`.fk-navrail__item .icon { width: 16px }` beats the width attribute).
const Icon = ({ children, size = 18 }: { children: ReactNode; size?: number }) => (
  <svg
    className="icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

// Test tube — upright lab tube with rounded bottom, a liquid line and
// a single rising bubble. Signals the experimentation/refinement loop.
const IconRefinement = () => (
  <Icon>
    {/* Rim */}
    <path d="M7 2.5h10" />
    {/* Tube body with rounded bottom */}
    <path d="M8.5 2.5v15a3.5 3.5 0 0 0 7 0v-15" />
    {/* Liquid line */}
    <path d="M8.8 13h6.4" />
    {/* Bubble */}
    <circle cx="12" cy="16" r="1.1" />
  </Icon>
);
// History — clock face with a counter-clockwise rewind arrow.
const IconHistory = () => (
  <Icon>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v4h4" />
    <path d="M12 8v4l3 2" />
  </Icon>
);
// Skills — a four-pointed sparkle: situational, learnable spark of capability.
const IconSkills = () => (
  <Icon>
    <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
    <path d="M12 8l1.2 2.8L16 12l-2.8 1.2L12 16l-1.2-2.8L8 12l2.8-1.2L12 8z" />
  </Icon>
);
const IconWorkflows = () => (
  <Icon>
    <circle cx="6" cy="6" r="2" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="18" cy="12" r="2" />
    <path d="M8 7l8 4M8 17l8-4" />
  </Icon>
);
const IconAgents = () => (
  <Icon>
    <rect x="4" y="7" width="16" height="12" rx="2" />
    <path d="M9 12h.01M15 12h.01" />
    <path d="M12 3v4" />
  </Icon>
);
const IconSessions = () => (
  <Icon>
    <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v6A2.5 2.5 0 0 1 16.5 15H10l-3.5 3v-3H7.5A2.5 2.5 0 0 1 5 12.5v-6z" />
  </Icon>
);
const IconBenchmarks = () => (
  <Icon>
    <path d="M5 19V11M12 19V5M19 19v-6" />
    <path d="M3 19h18" />
  </Icon>
);
const IconFilesystems = () => (
  <Icon>
    <path d="M4 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />
    <path d="M4 11h16" />
  </Icon>
);
const IconMissionSpecs = () => (
  <Icon>
    <path d="M8 4h8l3 3v13a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
    <path d="M15 4v4h4" />
    <path d="M9 13h6M9 17h4" />
  </Icon>
);
const IconSignals = () => (
  <Icon>
    <path d="M5 15a7 7 0 0 1 14 0" />
    <path d="M8 15a4 4 0 0 1 8 0" />
    <circle cx="12" cy="16.5" r="1.2" />
  </Icon>
);
// Managed Agents — a small agent perched on a server rack.
const IconManagedAgents = () => (
  <Icon>
    <path d="M12 2.2v2" />
    <circle cx="12" cy="1.8" r="0.7" />
    <rect x="8.5" y="4.2" width="7" height="5" rx="1.4" />
    <path d="M10.6 6.4h.01M13.4 6.4h.01" />
    <path d="M10 9.2h4" />
    <path d="M9.2 9.2v2.3M14.8 9.2v2.3" />
    <rect x="3" y="11.5" width="18" height="9.5" rx="1.4" />
    <path d="M6 14.2h6M6 17h6M6 19.5h6" />
    <path d="M15.5 14.2h.01M15.5 17h.01M15.5 19.5h.01" />
  </Icon>
);
const IconModels = () => (
  <Icon>
    <rect x="5" y="5" width="14" height="14" rx="2" />
    <path d="M9 9h6v6H9z" />
    <path d="M5 9h2M5 15h2M17 9h2M17 15h2M9 5v2M15 5v2M9 17v2M15 17v2" />
  </Icon>
);
const IconEvals = () => (
  <Icon>
    <path d="M9 4h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
);
const IconTraces = () => (
  <Icon>
    {/* Nested waterfall bars — a tiny flame graph */}
    <path d="M4 6h16" />
    <path d="M6 11h9" />
    <path d="M9 16h8" />
    <path d="M12 21h4" />
  </Icon>
);
const IconFeedback = () => (
  <Icon>
    {/* Chat bubble with thumbs-up */}
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <path d="M10 14v-1a1 1 0 0 1 1-1h0a1 1 0 0 0 1-1V9.5a1.5 1.5 0 0 1 3 0V11" />
    <path d="M14 11h1.5a1 1 0 0 1 1 1v0a1 1 0 0 1-1 1H11" />
  </Icon>
);
const IconGroundTruth = () => (
  <Icon>
    {/* Bullseye — the canonical "correct answer" workflows aim at */}
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5.5" />
    <circle cx="12" cy="12" r="2" />
  </Icon>
);
const IconJudges = () => (
  <Icon>
    {/* Scales of justice */}
    <path d="M12 4v16" />
    <path d="M6 20h12" />
    <path d="M5 7h14" />
    <path d="M8 4h8" />
    <path d="M5 7l-2 6a3 3 0 0 0 6 0L7 7" />
    <path d="M19 7l-2 6a3 3 0 0 0 6 0L21 7" />
  </Icon>
);
const IconSuites = () => (
  <Icon>
    {/* Stacked layers */}
    <path d="M12 4L3 9l9 5 9-5-9-5z" />
    <path d="M3 14l9 5 9-5" />
    <path d="M3 19l9 5 9-5" />
  </Icon>
);
const IconAppConnections = () => (
  <Icon>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </Icon>
);
const IconMcps = () => (
  <Icon>
    <path d="M9 4v4M15 4v4" />
    <rect x="6" y="8" width="12" height="6" rx="1" />
    <path d="M12 14v3a3 3 0 0 0 3 3h0" />
  </Icon>
);
const IconDocumentSets = () => (
  <Icon>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    <path d="M8 13h8M8 16h5" />
  </Icon>
);
const IconKey = () => (
  <Icon>
    <circle cx="8" cy="8" r="4" />
    <path d="M11 11l8 8M16 16l2-2M19 19l2-2" />
  </Icon>
);
// Remote Realtime Tools — classic lightning bolt. Suggests low-latency /
// realtime dispatch from the MCP server out to remote sources.
const IconLightning = () => (
  <Icon>
    <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
  </Icon>
);
// Orchestrator: a central node with three radiating connectors — the
// "watches everything" surface that interjects on duplicates. (DEV-16226.)
const IconOrchestrator = () => (
  <Icon>
    <circle cx="12" cy="12" r="2.5" />
    <circle cx="4.5" cy="6" r="1.5" />
    <circle cx="19.5" cy="6" r="1.5" />
    <circle cx="12" cy="20" r="1.5" />
    <path d="M11 10.5 5.5 6.8" />
    <path d="M13 10.5 18.5 6.8" />
    <path d="M12 14.5V18.5" />
  </Icon>
);
// Stylized memory chip — a square with notched contacts on each side, evoking
// both a memory module and a storage cell. Distinct from `IconBoard`'s kanban
// rectangles so the nav rail stays scannable at icon-only sizes.
const IconMemory = () => (
  <Icon>
    <rect x="5" y="5" width="14" height="14" rx="1.5" />
    <rect x="9" y="9" width="6" height="6" rx="0.5" />
    <path d="M8 3v2M12 3v2M16 3v2M8 19v2M12 19v2M16 19v2M3 8h2M3 12h2M3 16h2M19 8h2M19 12h2M19 16h2" />
  </Icon>
);
const IconBoard = () => (
  <Icon>
    <rect x="3" y="4" width="6" height="16" rx="1" />
    <rect x="11" y="4" width="6" height="9" rx="1" />
    <rect x="11" y="15" width="6" height="5" rx="1" />
  </Icon>
);
const IconDashboards = () => (
  <Icon>
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="5" rx="1" />
    <rect x="13" y="10" width="8" height="11" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
  </Icon>
);
// Single full-page dashboard — one frame filling the canvas.
const IconDashboardSingle = () => (
  <Icon>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18" />
  </Icon>
);
// Star — the Dashboards > Favorites folder.
const IconStar = () => (
  <Icon>
    <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2-5.5-2.9-5.5 2.9 1-6.2L3 9.6l6.2-.9z" />
  </Icon>
);
// Shine — four-point sparkle marking a dashboard updated within the last day.
// Deliberately NOT the shared `Icon` wrapper: its `.icon` class would get the
// navrail's 16px leading-glyph sizing; this is a small trailing adornment.
const IconShine = () => (
  <svg
    width={11}
    height={11}
    viewBox="0 0 24 24"
    fill="var(--status-warning)"
    stroke="none"
    role="img"
    aria-label="Updated in the last day"
    style={{ flexShrink: 0 }}
  >
    <title>Updated in the last day</title>
    <path d="M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z" />
  </svg>
);
const IconPipelines = () => (
  <Icon>
    {/* Branching pipeline: source node → split → two destinations */}
    <circle cx="5" cy="12" r="2" />
    <circle cx="19" cy="6" r="2" />
    <circle cx="19" cy="18" r="2" />
    <path d="M7 12h4a2 2 0 0 0 2-2V8a2 2 0 0 1 2-2h2" />
    <path d="M7 12h4a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2h2" />
  </Icon>
);
const IconOrgs = () => (
  <Icon>
    <path d="M4 21V8l5-4 5 4v13" />
    <path d="M14 21V12h6v9" />
    <path d="M9 9v0M9 13v0M9 17v0" />
  </Icon>
);
const IconUsers = () => (
  <Icon>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
    <path d="M16 5.5a3 3 0 0 1 0 5" />
    <path d="M18 14a5 5 0 0 1 3 4.5V20" />
  </Icon>
);
const IconBlueprints = () => (
  <Icon>
    {/* Blueprint document */}
    <path d="M5 3h11l4 4v14H5z" />
    <path d="M16 3v4h4" />
    <path d="M8 12h8" />
    <path d="M8 16h5" />
  </Icon>
);
const IconKnowledgeGraph = () => (
  <Icon>
    {/* Three connected nodes — a tiny knowledge graph */}
    <circle cx="6" cy="6" r="2.2" />
    <circle cx="18" cy="6" r="2.2" />
    <circle cx="12" cy="18" r="2.2" />
    <path d="M8 7l8 0" />
    <path d="M7 8l4 8" />
    <path d="M17 8l-4 8" />
  </Icon>
);
// Open book — the Knowledge folder: the library the pipelines distill
// document sets into (kept distinct from IconKnowledgeGraph, its child).
const IconKnowledge = () => (
  <Icon>
    <path d="M12 6c-1.6-1.4-3.6-2-6-2v14c2.4 0 4.4.6 6 2 1.6-1.4 3.6-2 6-2V4c-2.4 0-4.4.6-6 2z" />
    <path d="M12 6v14" />
  </Icon>
);

// ── Nav definition ─────────────────────────────────────────────────────────
// Blueprints / Pipelines / Knowledge graph stay superuser-gated (their routes
// still live under /internal with a beforeLoad guard) until the org-level
// feature-flag mechanism replaces that gate — they already sit in their
// customer-facing Knowledge home so the flip is data-only.

// Platform Costs — LLM/agent cost, usage, and execution detail.
// Spans cover workflow runs, persistent-agent wakes, chat, and evals, so this
// sits as its own top-level surface rather than under any single producer.
// Flag-gated (2026-07-09) like the other not-yet-customer-ready sections.
const platformCostsSection: INavSection = {
  label: 'Platform Costs',
  entries: [
    { label: 'Platform Costs', to: routes.PLATFORM_COSTS, prefix: '/traces', icon: <IconTraces /> },
  ],
};

const refinementSection: INavSection = {
  label: 'Refinement',
  entries: [
    {
      label: 'Refinement',
      to: routes.REFINEMENT_OVERVIEW,
      prefix: '/refinement/overview',
      icon: <IconRefinement />,
    },
    {
      label: 'History',
      to: routes.REFINEMENT,
      prefix: '/refinement/history',
      icon: <IconHistory />,
    },
    {
      label: 'Schedules',
      to: routes.REFINEMENT_SCHEDULES,
      prefix: '/refinement/schedules',
      icon: <IconBoard />,
    },
  ],
};

const orchestratorEntry: INavLink = {
  // Single entry — Settings/Live/Analytics surface as tabs at the top of
  // every /orchestrator/* page (OrchestratorTabs).
  label: 'Deduplication',
  to: routes.ORCHESTRATOR,
  prefix: '/orchestrator',
  icon: <IconOrchestrator />,
};

// Homed in Admin per the nav review ("should be on the admin page … not on
// the internal page"), flagged off until it's customer-ready. Stays
// superuser-gated too — the route still lives under /internal's guard.
const remoteRealtimeToolsEntry: INavLink = {
  label: 'Remote Realtime Tools',
  to: routes.INTERNAL_REMOTE_REALTIME_TOOLS,
  prefix: '/internal/remote-realtime-tools',
  icon: <IconLightning />,
  superuserOnly: true,
};

// Three sibling pages (2026-07-09 review follow-up: sidebar entries, not
// in-page tabs). All live under the /inbox path family; most-specific
// prefix wins in findActiveTo, so Board/Coordinator highlight correctly
// even though Inbox's prefix contains them. Board-columns admin is
// reached from the board's own toolbar, so it stays off the rail.
function buildIncidentsSection(): INavSection {
  return {
    label: 'Incidents',
    entries: [
      { label: 'Board', to: routes.INBOX_BOARD, prefix: '/inbox/board', icon: <IconBoard /> },
      {
        label: 'Coordinator',
        to: routes.INBOX_COORDINATOR,
        prefix: '/inbox/coordinator',
        icon: <IconOrchestrator />,
      },
    ],
  };
}

// AI-managed dashboards — agents author them; operators just watch. Its own
// top-level section (not under Incidents): "All dashboards" leads, then two
// folders — Recent (dashboards created or updated within the last day,
// excluding favorites since those already have their own folder) and
// Favorites (the caller's starred dashboards, starred on the All dashboards
// page). Both folders display alphabetically, and a dashboard updated within
// the last day that hasn't been opened since carries a shine mark (opens are
// per-browser localStorage, see dashboard-recency.ts; selection logic + full
// rules in lib/dashboard-nav.ts). Empty folders are dropped by
// visibleSections, so an org with nothing new just sees the link.
function buildDashboardsSection(
  dashboards: DashboardListItem[],
  visits: Record<string, number>,
): INavSection {
  const link = (d: DashboardListItem): INavLink => ({
    label: d.name,
    to: routes.dashboardDetail(d.id),
    prefix: routes.dashboardDetail(d.id),
    icon: d.kind === 'single' ? <IconDashboardSingle /> : <IconDashboards />,
    trailingIcon: hasUnseenUpdate(d, visits) ? <IconShine /> : undefined,
  });
  const recent = selectRecentDashboards(dashboards).map(link);
  const favorites = selectFavoriteDashboards(dashboards).map(link);
  return {
    label: 'Dashboards',
    entries: [
      {
        label: 'All dashboards',
        to: routes.DASHBOARDS,
        prefix: '/dashboards',
        icon: <IconDashboards />,
      },
      ...(recent.length > 0
        ? [{ label: 'Recent', icon: <IconHistory />, children: recent, alwaysExpanded: true }]
        : []),
      ...(favorites.length > 0
        ? [
            {
              label: 'Favorites',
              icon: <IconStar />,
              children: favorites,
              alwaysExpanded: true,
            },
          ]
        : []),
    ],
  };
}

function buildConfigSection(flags: ConsoleFeatureFlags): INavSection {
  return {
    label: 'Automation',
    entries: [
      {
        // Collapsible folder. Skills/Memories keep their /agents/* homes.
        // Benchmarks, Filesystems, Mission Specs, and Signals are their own
        // pages here. Most-specific prefix wins over Agents at /agents.
        label: 'Managed Agents',
        icon: <IconManagedAgents />,
        children: [
          { label: 'Agents', to: routes.AGENTS, prefix: '/agents', icon: <IconAgents /> },
          ...(flags.persistentAgentOrchestration
            ? [
                {
                  label: 'Sessions',
                  to: routes.AGENTS_SESSIONS,
                  prefix: '/agents/sessions',
                  icon: <IconSessions />,
                },
              ]
            : []),
          { label: 'Skills', to: routes.SKILLS, prefix: '/agents/skills', icon: <IconSkills /> },
          {
            label: 'Memories',
            to: routes.MEMORY,
            prefix: '/agents/memories',
            icon: <IconMemory />,
          },
          {
            label: 'Collections',
            to: routes.COLLECTIONS,
            prefix: '/agents/collections',
            icon: <IconAgents />,
          },
          ...(flags.persistentAgentOrchestration
            ? [
                {
                  label: 'Benchmarks',
                  to: routes.ORCHESTRATION_BENCHMARKS,
                  prefix: '/agents/orchestration/benchmarks',
                  icon: <IconBenchmarks />,
                },
                {
                  label: 'Filesystems',
                  to: routes.ORCHESTRATION_FILESYSTEMS,
                  prefix: '/agents/orchestration/filesystems',
                  icon: <IconFilesystems />,
                },
                {
                  label: 'Mission Specs',
                  to: routes.ORCHESTRATION_SPECS,
                  prefix: '/agents/orchestration/specs',
                  icon: <IconMissionSpecs />,
                },
                {
                  label: 'Signals',
                  to: routes.ORCHESTRATION_SIGNALS,
                  prefix: '/agents/orchestration/signals',
                  icon: <IconSignals />,
                },
              ]
            : []),
          // Auto-approve is hidden from the nav (the /agents/auto-approve route
          // still exists and is reachable directly). Re-add here to resurface.
        ],
      },
      { label: 'Workflows', to: routes.WORKFLOWS, prefix: '/workflows', icon: <IconWorkflows /> },
      {
        // Org-wide cron schedules for workflows (DEV-17470). Sits next to
        // Workflows because that's where people go to make one fire; the
        // refinement Schedules page links here via its tab strip too.
        label: 'Schedules',
        to: routes.WORKFLOW_SCHEDULES,
        prefix: '/schedules',
        icon: <IconBoard />,
      },
      {
        // Evals (the results surface) leads — it's the landing page for the
        // folder and what the folder is named for. Suites (which absorbed the
        // Tasks page) and Feedback follow; Judges + Ground truths are the task
        // prerequisites, so they keep their spots at the tail.
        label: 'Evals',
        icon: <IconEvals />,
        children: [
          { label: 'Evals', to: routes.EVALS, prefix: '/evals', icon: <IconEvals /> },
          { label: 'Suites', to: routes.SUITES, prefix: '/suites', icon: <IconSuites /> },
          { label: 'Feedback', to: routes.FEEDBACK, prefix: '/feedback', icon: <IconFeedback /> },
          { label: 'Judges', to: routes.JUDGES, prefix: '/judges', icon: <IconJudges /> },
          {
            label: 'Ground truths',
            to: routes.GROUND_TRUTH,
            prefix: '/ground-truth',
            icon: <IconGroundTruth />,
          },
        ],
      },
      {
        // Ordered as the data flows: upload document sets, author blueprints,
        // run pipelines, land in the knowledge graph.
        label: 'Knowledge',
        icon: <IconKnowledge />,
        children: [
          {
            // v2 Knowledge bases (Documents → Knowledge graph) — presentation
            // layer on top of Document sets. See docs/proposals/documents-skills-kg-proposal-v2.md.
            // URL stays /workspaces; only the label changed in the 2026-07 demo review.
            label: 'Knowledge bases',
            to: '/workspaces',
            prefix: '/workspaces',
            icon: <IconKnowledge />,
          },
          {
            // Org-level catalog of document-processing skills. Created here, then
            // attached to a knowledge base from its Documents tab.
            label: 'Document processing skills',
            to: '/skills',
            prefix: '/skills',
            icon: <IconBlueprints />,
          },
          // The standalone "Knowledge graph" explorer was removed from the nav in
          // the 2026-07 demo review — the graph is only meaningful inside a
          // knowledge base, where it lives as the "Knowledge graph" tab. The
          // route + view still exist (the KB tab imports KnowledgeGraphsView);
          // the pipeline-owned explorer stays under Legacy below.
          // Legacy, pre-workspace surfaces. Still fully functional and
          // reachable — Phase 5/6 of the v2 plan deferred their migration and
          // deletion indefinitely — but tucked into a collapsible "Legacy"
          // sub-group at the tail of the Knowledge folder so the Workspaces
          // path stays the obvious one. `subgroup` drives the toggle.
          {
            label: 'Document sets',
            to: routes.DOCUMENT_SETS,
            prefix: '/document-sets',
            icon: <IconDocumentSets />,
            subgroup: 'Legacy',
          },
          {
            label: 'Blueprints',
            to: routes.INTERNAL_BLUEPRINTS,
            prefix: '/internal/blueprints',
            icon: <IconBlueprints />,
            superuserOnly: true,
            subgroup: 'Legacy',
          },
          {
            label: 'Pipelines',
            to: routes.INTERNAL_PIPELINES,
            prefix: '/internal/pipelines',
            icon: <IconPipelines />,
            superuserOnly: true,
            subgroup: 'Legacy',
          },
          {
            // Same explorer, but pipelines stay in the owner picker.
            label: 'Knowledge graph (legacy)',
            to: routes.INTERNAL_KNOWLEDGE_GRAPHS,
            prefix: '/internal/knowledge-graphs',
            icon: <IconKnowledgeGraph />,
            subgroup: 'Legacy',
          },
        ],
      },
    ],
  };
}

/**
 * Assemble the rail's sections for the org's resolved feature flags. Incidents
 * leads; flagged sections slot in only when their org-level flag is on.
 */
function buildNavSections(
  flags: ConsoleFeatureFlags,
  dashboards: DashboardListItem[],
  dashboardVisits: Record<string, number>,
): INavSection[] {
  return [
    buildIncidentsSection(),
    ...(flags.persistentAgentOrchestration
      ? [buildDashboardsSection(dashboards, dashboardVisits)]
      : []),
    buildConfigSection(flags),
    ...(flags.platformCosts ? [platformCostsSection] : []),
    ...(flags.refinement ? [refinementSection] : []),
    {
      label: 'Admin',
      entries: [
        { label: 'Models', to: routes.MODELS, prefix: '/models', icon: <IconModels /> },
        {
          label: 'MCPs',
          to: routes.MCP_CONNECTIONS,
          prefix: '/mcp-connections',
          icon: <IconMcps />,
        },
        {
          label: 'App Connections',
          to: routes.APP_CONNECTIONS,
          prefix: '/app-connections',
          icon: <IconAppConnections />,
        },
        { label: 'API keys', to: routes.API_KEYS, prefix: '/api-keys', icon: <IconKey /> },
        {
          label: 'Users',
          to: routes.USERS,
          prefix: '/users',
          icon: <IconUsers />,
          coherenceHidden: true,
        },
        ...(flags.deduplication ? [orchestratorEntry] : []),
        ...(flags.remoteRealtimeTools ? [remoteRealtimeToolsEntry] : []),
      ],
    },
    {
      label: 'Internal',
      superuserOnly: true,
      entries: [
        {
          label: 'Organizations',
          to: routes.INTERNAL_ORGS,
          prefix: '/internal/organizations',
          icon: <IconOrgs />,
          coherenceHidden: true,
        },
      ],
    },
  ];
}

function visibleSections(
  navSections: INavSection[],
  isSuper: boolean,
  isFde: boolean,
  isCoherence: boolean,
): INavSection[] {
  if (!isFde) return [];
  // A link survives if it isn't superuser-gated away and isn't Coherence-hidden
  // in a Coherence session. Emptied folders/sections are dropped afterward.
  const linkVisible = (link: INavLink): boolean =>
    (!link.superuserOnly || isSuper) && !(link.coherenceHidden && isCoherence);
  return navSections
    .filter((section) => !section.superuserOnly || isSuper)
    .map((section) => ({
      ...section,
      entries: section.entries
        .map((entry) =>
          isFolder(entry) ? { ...entry, children: entry.children.filter(linkVisible) } : entry,
        )
        .filter((entry) => (isFolder(entry) ? entry.children.length > 0 : linkVisible(entry))),
    }))
    .filter((section) => section.entries.length > 0);
}

// Most-specific match wins: when two pages both match (e.g. Builder at
// `/workflows` and Agents at `/workflows/agents`), only the longest-matching
// one is active.
function findActiveTo(sections: INavSection[], pathname: string): string | null {
  let best: string | null = null;
  let bestScore = -1;
  const consider = (page: INavLink) => {
    if (pathname === page.to || pathname.startsWith(`${page.prefix}/`)) {
      const score = Math.max(page.to.length, page.prefix.length);
      if (score > bestScore) {
        bestScore = score;
        best = page.to;
      }
    }
  };
  for (const section of sections) {
    for (const entry of section.entries) {
      if (isFolder(entry)) {
        entry.children.forEach(consider);
      } else {
        consider(entry);
      }
    }
  }
  return best;
}

// ── Components ────────────────────────────────────────────────────────────

const chevronDown = (
  <svg
    className="item-chevron"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

function NavItem({
  page,
  collapsed,
  child = false,
  isActive,
  onNavigate,
}: {
  page: INavLink;
  collapsed: boolean;
  child?: boolean;
  isActive: boolean;
  onNavigate: () => void;
}) {
  const classes = ['fk-navrail__item'];
  if (child) classes.push('fk-navrail__item--child');
  if (isActive) classes.push('is-active');
  if (collapsed) classes.push('fk-tip', 'fk-tip--right');
  return (
    <Link
      to={page.to}
      className={classes.join(' ')}
      data-tip={collapsed ? page.label : undefined}
      aria-label={page.label}
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate}
    >
      {page.icon}
      <span className="item-label">{page.label}</span>
      {page.trailingIcon ?? null}
      {page.badge ? <span className="item-count">{page.badge}</span> : null}
    </Link>
  );
}

function NavFolder({
  folder,
  collapsed,
  activeTo,
  expanded,
  onToggle,
  onNavigate,
}: {
  folder: INavFolder;
  collapsed: boolean;
  activeTo: string | null;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const first = folder.children[0];
  const containsActive = folder.children.some((c) => c.to === activeTo);
  if (collapsed) {
    // Pinned-open folders (Dashboards > Recent/Favorites) are curated
    // quick-launch lists that only make sense expanded — in the collapsed
    // rail we drop them entirely rather than surfacing a stand-in icon.
    if (folder.alwaysExpanded === true) return null;
    // Collapsed rails show one icon per folder; it links to the folder's
    // first page as the section entry point (children are hidden by CSS).
    if (!first) return null;
    return (
      <NavItem
        page={{ ...first, label: folder.label, icon: folder.icon }}
        collapsed={true}
        isActive={containsActive}
        onNavigate={onNavigate}
      />
    );
  }
  // Pinned-open folders render a static header: no chevron, no toggle.
  const pinned = folder.alwaysExpanded === true;
  return (
    <div className={expanded ? 'fk-navrail__folder is-expanded' : 'fk-navrail__folder'}>
      <button
        type="button"
        className="fk-navrail__item"
        aria-expanded={expanded}
        onClick={pinned ? undefined : onToggle}
        style={pinned ? { cursor: 'default' } : undefined}
      >
        {folder.icon}
        <span className="item-label">{folder.label}</span>
        {pinned ? null : chevronDown}
      </button>
      {expanded ? (
        <div className="fk-navrail__children">
          {folder.children
            .filter((page) => !page.subgroup)
            .map((page) => (
              <NavItem
                key={page.to}
                page={page}
                collapsed={false}
                child={true}
                isActive={page.to === activeTo}
                onNavigate={onNavigate}
              />
            ))}
          {groupSubgroups(folder.children).map(([label, items]) => (
            <NavSubgroup
              key={label}
              label={label}
              items={items}
              activeTo={activeTo}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Collect a folder's subgrouped children into ordered `[label, items]` pairs,
 * preserving first-seen label order. Items without a `subgroup` are ignored
 * (they render inline above the subgroups).
 */
function groupSubgroups(children: INavLink[]): Array<[string, INavLink[]]> {
  const groups = new Map<string, INavLink[]>();
  for (const c of children) {
    if (!c.subgroup) continue;
    const arr = groups.get(c.subgroup) ?? [];
    arr.push(c);
    groups.set(c.subgroup, arr);
  }
  return [...groups.entries()];
}

/**
 * A collapsible sub-group nested inside an expanded folder — one toggle hiding
 * a cluster of related child links (e.g. the demoted "Legacy" surfaces under
 * Knowledge). Mirrors the folder's own auto-open-when-active behavior.
 */
function NavSubgroup({
  label,
  items,
  activeTo,
  onNavigate,
}: {
  label: string;
  items: INavLink[];
  activeTo: string | null;
  onNavigate: () => void;
}) {
  const containsActive = items.some((c) => c.to === activeTo);
  const [open, setOpen] = useState(false);
  const expanded = open || containsActive;
  return (
    <div className={expanded ? 'fk-navrail__subgroup is-expanded' : 'fk-navrail__subgroup'}>
      <button
        type="button"
        className="fk-navrail__item fk-navrail__subgroup-toggle"
        aria-expanded={expanded}
        onClick={() => setOpen((v) => !v)}
        style={{ paddingLeft: 12 }}
      >
        <span
          className="item-label"
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            opacity: 0.7,
          }}
        >
          {label}
        </span>
        {chevronDown}
      </button>
      {expanded
        ? items.map((page) => (
            <NavItem
              key={page.to}
              page={page}
              collapsed={false}
              child={true}
              isActive={page.to === activeTo}
              onNavigate={onNavigate}
            />
          ))
        : null}
    </div>
  );
}

export function ConsoleNavRail({ collapsed }: { collapsed: boolean }) {
  const isSuper = useIsSuperuser();
  const isFde = useOrgRole() === 'fde';
  // Coherence sessions hide Keycloak-only admin surfaces (Users, Organizations).
  const isCoherence = getAuthMode() === 'coherence';
  const flags = useFeatureFlags();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navDashboards = useNavDashboards();
  // Live external store of visit times — the rail re-renders the instant the
  // dashboard detail route records a visit, so opening a dashboard reorders
  // "Recent" and clears its unseen-update shine immediately (not on the next
  // navigation).
  const dashboardVisits = useSyncExternalStore(subscribeDashboardVisits, readDashboardVisits);
  const sections = useMemo(
    () =>
      visibleSections(
        buildNavSections(flags, navDashboards, dashboardVisits),
        isSuper,
        isFde,
        isCoherence,
      ),
    [flags, isSuper, isFde, isCoherence, navDashboards, dashboardVisits],
  );
  const activeTo = findActiveTo(sections, pathname);
  // Folders follow the active page by default (the folder holding it is
  // open, the rest are shut). A chevron click overrides that until the next
  // navigation, which clears the overrides so the rail tracks location again.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const clearToggles = () => setToggled({});

  return (
    <aside className={collapsed ? 'fk-navrail fk-navrail--collapsed' : 'fk-navrail'}>
      <nav className="fk-navrail__list" aria-label="Console pages">
        <BotsNavSection collapsed={collapsed} />
        {sections.map((section) => (
          <Fragment key={section.label}>
            <span className="fk-navrail__label">{section.label}</span>
            {section.entries.map((entry) =>
              isFolder(entry) ? (
                <NavFolder
                  key={entry.label}
                  folder={entry}
                  collapsed={collapsed}
                  activeTo={activeTo}
                  expanded={
                    entry.alwaysExpanded ??
                    toggled[entry.label] ??
                    entry.children.some((c) => c.to === activeTo)
                  }
                  onToggle={() =>
                    setToggled((t) => ({
                      ...t,
                      [entry.label]: !(
                        t[entry.label] ?? entry.children.some((c) => c.to === activeTo)
                      ),
                    }))
                  }
                  onNavigate={clearToggles}
                />
              ) : (
                <NavItem
                  key={entry.to}
                  page={entry}
                  collapsed={collapsed}
                  isActive={entry.to === activeTo}
                  onNavigate={clearToggles}
                />
              ),
            )}
          </Fragment>
        ))}
      </nav>
    </aside>
  );
}
